import React from 'react';
import { formatDateForDisplay } from '../../Helpers/DateUtils';
import { handedOutSince, isOverdue } from '../../Helpers/keyRegisterUtils';
import { KeyHolder, keyStatusLabel, RegisterKey } from '../../types/keyRegister';
import HolderAvatar from './HolderAvatar';
import { keySerialLabel } from './keyRegisterLabels';

interface KeyTableProps {
    keys: RegisterKey[];
    today: string;
    holderName: (holder: KeyHolder | undefined) => string;
    onSelectKey: (keyKey: string) => void;
}

const DASH = '—';

/** One cell with the label shown only on phones, where the table is a list of cards. */
const Cell: React.FC<{ area: string; label: string; children: React.ReactNode }> = ({ area, label, children }) => (
    <td className={`key-cell key-cell--${area}`}>
        <span className="key-cell-label">{label}</span>
        <span className="key-cell-value">{children}</span>
    </td>
);

/** The keys of one type. A row opens the key modal (click, Enter or Space). */
const KeyTable: React.FC<KeyTableProps> = ({ keys, today, holderName, onSelectKey }) => (
    <table className="key-table">
        <thead>
            <tr>
                <th scope="col">Nr</th>
                <th scope="col">Status</th>
                <th scope="col">Innehavare</th>
                <th scope="col">Sedan</th>
                <th scope="col">Åter senast</th>
                <th scope="col">Förvaring</th>
            </tr>
        </thead>
        <tbody>
            {keys.map(key => {
                const overdue = isOverdue(key, today);
                const since = handedOutSince(key);
                const open = () => onSelectKey(key.key);
                return (
                    <tr
                        key={key.key}
                        className={`key-row${key.status === 'scrapped' ? ' key-row--scrapped' : ''}`}
                        tabIndex={0}
                        data-testid={`key-row-${key.key}`}
                        aria-label={`${keySerialLabel(key)}, ${keyStatusLabel(key)}${overdue ? ', försenad' : ''}`}
                        onClick={open}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                open();
                            }
                        }}
                    >
                        <td className="key-cell key-cell--serial">
                            <span className="key-serial">{key.serial ? <><span className="key-serial-prefix">Nr </span>{key.serial}</> : DASH}</span>
                        </td>
                        <td className="key-cell key-cell--status">
                            <span className={`key-pill key-pill--${key.status}`}>{keyStatusLabel(key)}</span>
                        </td>
                        <Cell area="holder" label="Innehavare">
                            {key.status === 'handedOut' && key.holder ? <HolderAvatar name={holderName(key.holder)} /> : DASH}
                        </Cell>
                        <Cell area="since" label="Sedan">{since ? formatDateForDisplay(since) : DASH}</Cell>
                        <Cell area="due" label="Åter senast">
                            {key.status === 'handedOut' && key.dueDate ? (
                                <span className={overdue ? 'key-due key-due--overdue' : 'key-due'}>
                                    {formatDateForDisplay(key.dueDate)}{overdue && <span className="key-due-tag"> · försenad</span>}
                                </span>
                            ) : DASH}
                        </Cell>
                        <Cell area="location" label="Förvaring">{key.location || DASH}</Cell>
                    </tr>
                );
            })}
        </tbody>
    </table>
);

export default KeyTable;
